import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, homePath, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Loading } from "@/components/common";
import Login from "@/pages/Login";
import ChangePassword from "@/pages/ChangePassword";
import Dashboard from "@/pages/Dashboard";
import Purchases from "@/pages/Purchases";
import Sections from "@/pages/Sections";
import Customers from "@/pages/Customers";
import NewSale from "@/pages/NewSale";
import Sales from "@/pages/Sales";
import SaleDetails from "@/pages/SaleDetails";
import Payments from "@/pages/Payments";
import Permits from "@/pages/Permits";
import Barn from "@/pages/Barn";
import Gate from "@/pages/Gate";
import VerifyOnly from "@/pages/VerifyOnly";
import Reports from "@/pages/Reports";
import Expenses from "@/pages/Expenses";
import UsersPage from "@/pages/Users";
import SettingsPage from "@/pages/Settings";
import Audit from "@/pages/Audit";
import Backup from "@/pages/Backup";
import { PrintSale, PrintPayment, PrintPermit, PrintCustomer, PrintPermits } from "@/pages/PrintDocs";
import Lookup from "@/pages/Lookup";
import Chat from "@/pages/Chat";
import Assistant from "@/pages/Assistant";
import { ChatProvider } from "@/context/ChatContext";
import PenWorker from "@/pages/PenWorker";
import Selections from "@/pages/Selections";
import Treasuries from "@/pages/Treasuries";
import TreasuryDetail from "@/pages/TreasuryDetail";
import TreasuryReport from "@/pages/TreasuryReport";

const TP = ["view_all_treasuries", "manage_treasuries", "record_payment", "manage_expenses"];

function Private({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  return children;
}

function Guard({ perms, children }) {
  const { can } = useAuth();
  return !perms.length || can(...perms) ? children : <div className="k-card p-10 text-center font-bold text-rose-700" data-testid="no-access">ليست لديك صلاحية للوصول إلى هذه الصفحة</div>;
}

function Home() {
  const { user } = useAuth();
  return <Navigate to={homePath(user)} replace />;
}

const R = [
  ["dashboard", ["view_dashboard"], <Dashboard />], ["purchases", ["manage_inventory"], <Purchases />],
  ["sections", ["manage_sections"], <Sections />], ["customers", ["manage_customers", "create_sale"], <Customers />],
  ["sales/new", ["create_sale"], <NewSale />], ["sales", ["view_sales"], <Sales />], ["sales/:id", ["view_sales"], <SaleDetails />],
  ["payments", ["view_sales"], <Payments />], ["permits", ["issue_permit", "cancel_permit"], <Permits />],
  ["barn", ["confirm_delivery", "view_my_sections", "verify_permit"], <Barn />], ["gate", ["confirm_exit", "verify_permit"], <Gate />],
  ["verify", ["verify_permit"], <VerifyOnly />], ["lookup", ["lookup_sheep", "view_sales", "verify_permit", "record_selection"], <Lookup />], ["r/:token", ["lookup_sheep", "view_sales", "verify_permit", "record_selection"], <Lookup />], ["chat", [], <Chat />], ["assistant", [], <Assistant />], ["reports", ["view_dashboard"], <Reports />], ["expenses", ["manage_expenses"], <Expenses />],
  ["users", ["manage_users"], <UsersPage />], ["settings", ["manage_settings"], <SettingsPage />], ["audit", ["view_audit"], <Audit />],
  ["backup", ["backup"], <Backup />], ["pen", ["record_selection"], <PenWorker />],
  ["selections", ["create_sale", "manage_selections"], <Selections />],
  ["treasuries", TP, <Treasuries />], ["treasuries/:id", TP, <TreasuryDetail />], ["treasury-reports", TP, <TreasuryReport />],
];

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
      <ChatProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/print/sale/:id" element={<Private><Guard perms={["view_sales"]}><PrintSale /></Guard></Private>} />
          <Route path="/print/payment/:id" element={<Private><Guard perms={["view_sales"]}><PrintPayment /></Guard></Private>} />
          <Route path="/print/customer/:id" element={<Private><Guard perms={["view_sales"]}><PrintCustomer /></Guard></Private>} />
          <Route path="/print/permits" element={<Private><Guard perms={["issue_permit", "view_sales"]}><PrintPermits /></Guard></Private>} />
          <Route path="/print/permit/:id" element={<Private><Guard perms={["issue_permit", "view_sales"]}><PrintPermit /></Guard></Private>} />
          <Route element={<Private><Layout /></Private>}>
            <Route index element={<Home />} />
            {R.map(([p, perms, el]) => <Route key={p} path={p} element={<Guard perms={perms}>{el}</Guard>} />)}
            <Route path="no-access" element={<div className="k-card p-10 text-center">لا توجد صفحات متاحة لحسابك، يرجى مراجعة المدير.</div>} />
            <Route path="*" element={<Home />} />
          </Route>
        </Routes>
      </ChatProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors dir="rtl" />
    </AuthProvider>
  );
}
